import { supabase } from './supabase';

// ============================================
// CHECK-IN SERVICE
// ============================================

export const checkInService = {
  // Generate QR code data for member
  async generateQRCode(memberId) {
    try {
      const qrData = `CHECKIN_${memberId}_${Date.now()}`;
      return { data: qrData, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create member check-in
  async createCheckIn(checkInData) {
    try {
      const { data, error } = await supabase?.from('member_check_ins')?.insert([{
          gym_id: checkInData?.gymId,
          member_id: checkInData?.memberId,
          check_in_method: checkInData?.checkInMethod || 'qr_code',
          qr_code_data: checkInData?.qrCodeData,
          location: checkInData?.location,
          notes: checkInData?.notes
        }])?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update check-out time
  async checkOut(checkInId) {
    try {
      const { data, error } = await supabase?.from('member_check_ins')?.update({ check_out_time: new Date()?.toISOString() })?.eq('id', checkInId)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get member check-in history
  async getMemberCheckIns(memberId, limit = 20) {
    try {
      const { data, error } = await supabase?.from('member_check_ins')?.select(`
          *,
          member:user_profiles!member_id(id, full_name, email, avatar_url),
          gym:gyms(id, name)
        `)?.eq('member_id', memberId)?.order('check_in_time', { ascending: false })?.limit(limit);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get all check-ins for a gym
  async getGymCheckIns(gymId, date = null) {
    try {
      let query = supabase?.from('member_check_ins')?.select(`
          *,
          member:user_profiles!member_id(id, full_name, email, avatar_url),
          gym:gyms(id, name)
        `)?.eq('gym_id', gymId)?.order('check_in_time', { ascending: false });

      if (date) {
        const startOfDay = new Date(date);
        startOfDay?.setHours(0, 0, 0, 0);
        const endOfDay = new Date(date);
        endOfDay?.setHours(23, 59, 59, 999);

        query = query?.gte('check_in_time', startOfDay?.toISOString())?.lte('check_in_time', endOfDay?.toISOString());
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Subscribe to real-time check-ins
  subscribeToCheckIns(gymId, callback) {
    const channel = supabase?.channel(`check-ins-${gymId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'member_check_ins',
          filter: `gym_id=eq.${gymId}`
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return channel;
  }
};

// ============================================
// ATTENDANCE SERVICE
// ============================================

export const attendanceService = {
  // Clock in
  async clockIn(attendanceData) {
    try {
      const { data, error } = await supabase?.from('staff_attendance')?.insert([{
          gym_id: attendanceData?.gymId,
          staff_id: attendanceData?.staffId,
          attendance_date: attendanceData?.date || new Date()?.toISOString()?.split('T')?.[0],
          clock_in_time: new Date()?.toISOString(),
          status: 'clocked_in',
          location: attendanceData?.location,
          notes: attendanceData?.notes
        }])?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Clock out
  async clockOut(attendanceId) {
    try {
      const { data, error } = await supabase?.from('staff_attendance')?.update({
          clock_out_time: new Date()?.toISOString(),
          status: 'clocked_out'
        })?.eq('id', attendanceId)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Start break
  async startBreak(attendanceId) {
    try {
      const { data, error } = await supabase?.from('staff_attendance')?.update({
          break_start_time: new Date()?.toISOString(),
          status: 'on_break'
        })?.eq('id', attendanceId)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // End break
  async endBreak(attendanceId) {
    try {
      // Get current record to calculate break duration
      const { data: currentRecord } = await supabase?.from('staff_attendance')?.select('break_start_time, break_duration_minutes')?.eq('id', attendanceId)?.single();

      if (currentRecord?.break_start_time) {
        const breakStart = new Date(currentRecord.break_start_time);
        const breakEnd = new Date();
        const breakMinutes = Math.floor((breakEnd - breakStart) / 60000);

        const { data, error } = await supabase?.from('staff_attendance')?.update({
            break_end_time: breakEnd?.toISOString(),
            break_duration_minutes: (currentRecord?.break_duration_minutes || 0) + breakMinutes,
            status: 'clocked_in'
          })?.eq('id', attendanceId)?.select()?.single();

        return { data, error };
      }

      return { data: null, error: { message: 'No active break found' } };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get staff attendance records
  async getStaffAttendance(staffId, startDate = null, endDate = null) {
    try {
      let query = supabase?.from('staff_attendance')?.select(`
          *,
          staff:user_profiles!staff_id(id, full_name, email, avatar_url, role),
          gym:gyms(id, name)
        `)?.eq('staff_id', staffId)?.order('attendance_date', { ascending: false });

      if (startDate) {
        query = query?.gte('attendance_date', startDate);
      }
      if (endDate) {
        query = query?.lte('attendance_date', endDate);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get gym attendance records
  async getGymAttendance(gymId, date = null) {
    try {
      let query = supabase?.from('staff_attendance')?.select(`
          *,
          staff:user_profiles!staff_id(id, full_name, email, avatar_url, role),
          gym:gyms(id, name)
        `)?.eq('gym_id', gymId)?.order('clock_in_time', { ascending: false });

      if (date) {
        query = query?.eq('attendance_date', date);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get current active attendance
  async getCurrentAttendance(staffId) {
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const { data, error } = await supabase?.from('staff_attendance')?.select('*')?.eq('staff_id', staffId)?.eq('attendance_date', today)?.is('clock_out_time', null)?.order('clock_in_time', { ascending: false })?.limit(1)?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Subscribe to real-time attendance updates
  subscribeToAttendance(gymId, callback) {
    const channel = supabase?.channel(`attendance-${gymId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'staff_attendance',
          filter: `gym_id=eq.${gymId}`
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return channel;
  }
};

// ============================================
// PAYROLL SERVICE
// ============================================

export const payrollService = {
  // Create payroll record
  async createPayrollRecord(payrollData) {
    try {
      const { data, error } = await supabase?.from('payroll_records')?.insert([{
          gym_id: payrollData?.gymId,
          staff_id: payrollData?.staffId,
          hr_profile_id: payrollData?.hrProfileId,
          pay_period_start: payrollData?.payPeriodStart,
          pay_period_end: payrollData?.payPeriodEnd,
          regular_hours: payrollData?.regularHours || 0,
          overtime_hours: payrollData?.overtimeHours || 0,
          bonus: payrollData?.bonus || 0,
          commission: payrollData?.commission || 0,
          tax_deductions: payrollData?.taxDeductions || 0,
          benefit_deductions: payrollData?.benefitDeductions || 0,
          other_deductions: payrollData?.otherDeductions || 0,
          notes: payrollData?.notes
        }])?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update payroll record
  async updatePayrollRecord(payrollId, updates) {
    try {
      const updateData = {};
      if (updates?.regularHours !== undefined) updateData.regular_hours = updates?.regularHours;
      if (updates?.overtimeHours !== undefined) updateData.overtime_hours = updates?.overtimeHours;
      if (updates?.bonus !== undefined) updateData.bonus = updates?.bonus;
      if (updates?.commission !== undefined) updateData.commission = updates?.commission;
      if (updates?.taxDeductions !== undefined) updateData.tax_deductions = updates?.taxDeductions;
      if (updates?.benefitDeductions !== undefined) updateData.benefit_deductions = updates?.benefitDeductions;
      if (updates?.otherDeductions !== undefined) updateData.other_deductions = updates?.otherDeductions;
      if (updates?.status !== undefined) updateData.status = updates?.status;
      if (updates?.notes !== undefined) updateData.notes = updates?.notes;

      const { data, error } = await supabase?.from('payroll_records')?.update(updateData)?.eq('id', payrollId)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get staff payroll records
  async getStaffPayroll(staffId) {
    try {
      const { data, error } = await supabase?.from('payroll_records')?.select(`
          *,
          staff:user_profiles!staff_id(id, full_name, email),
          hr_profile:hr_staff_profiles(employee_number, position, hourly_rate),
          gym:gyms(id, name)
        `)?.eq('staff_id', staffId)?.order('pay_period_end', { ascending: false });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get gym payroll records
  async getGymPayroll(gymId, status = null) {
    try {
      let query = supabase?.from('payroll_records')?.select(`
          *,
          staff:user_profiles!staff_id(id, full_name, email),
          hr_profile:hr_staff_profiles(employee_number, position, hourly_rate),
          gym:gyms(id, name)
        `)?.eq('gym_id', gymId)?.order('pay_period_end', { ascending: false });

      if (status) {
        query = query?.eq('status', status);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Approve payroll
  async approvePayroll(payrollId, approverId) {
    try {
      const { data, error } = await supabase?.from('payroll_records')?.update({
          status: 'approved',
          approved_by: approverId,
          approved_at: new Date()?.toISOString()
        })?.eq('id', payrollId)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Calculate payroll from attendance
  async calculatePayrollFromAttendance(staffId, hrProfileId, payPeriodStart, payPeriodEnd) {
    try {
      // Get attendance records for the period
      const { data: attendanceRecords } = await supabase?.from('staff_attendance')?.select('regular_hours, overtime_hours')?.eq('staff_id', staffId)?.gte('attendance_date', payPeriodStart)?.lte('attendance_date', payPeriodEnd);

      if (!attendanceRecords) {
        return { data: null, error: { message: 'No attendance records found' } };
      }

      const totalRegularHours = attendanceRecords?.reduce((sum, record) => sum + (parseFloat(record?.regular_hours) || 0), 0);
      const totalOvertimeHours = attendanceRecords?.reduce((sum, record) => sum + (parseFloat(record?.overtime_hours) || 0), 0);

      return {
        data: {
          regularHours: totalRegularHours,
          overtimeHours: totalOvertimeHours
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// ============================================
// HR SERVICE
// ============================================

export const hrService = {
  // Create HR profile
  async createHRProfile(profileData) {
    try {
      const { data, error } = await supabase?.from('hr_staff_profiles')?.insert([{
          staff_id: profileData?.staffId,
          gym_id: profileData?.gymId,
          employee_number: profileData?.employeeNumber,
          department: profileData?.department,
          position: profileData?.position,
          hire_date: profileData?.hireDate,
          employment_status: profileData?.employmentStatus || 'active',
          hourly_rate: profileData?.hourlyRate,
          salary: profileData?.salary,
          pay_frequency: profileData?.payFrequency,
          emergency_contact_name: profileData?.emergencyContactName,
          emergency_contact_phone: profileData?.emergencyContactPhone,
          emergency_contact_relationship: profileData?.emergencyContactRelationship,
          address: profileData?.address,
          city: profileData?.city,
          state: profileData?.state,
          zip_code: profileData?.zipCode,
          date_of_birth: profileData?.dateOfBirth,
          pto_balance_hours: profileData?.ptoBalanceHours || 0,
          sick_leave_balance_hours: profileData?.sickLeaveBalanceHours || 0
        }])?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update HR profile
  async updateHRProfile(profileId, updates) {
    try {
      const updateData = {};
      if (updates?.department !== undefined) updateData.department = updates?.department;
      if (updates?.position !== undefined) updateData.position = updates?.position;
      if (updates?.employmentStatus !== undefined) updateData.employment_status = updates?.employmentStatus;
      if (updates?.hourlyRate !== undefined) updateData.hourly_rate = updates?.hourlyRate;
      if (updates?.salary !== undefined) updateData.salary = updates?.salary;
      if (updates?.payFrequency !== undefined) updateData.pay_frequency = updates?.payFrequency;
      if (updates?.emergencyContactName !== undefined) updateData.emergency_contact_name = updates?.emergencyContactName;
      if (updates?.emergencyContactPhone !== undefined) updateData.emergency_contact_phone = updates?.emergencyContactPhone;
      if (updates?.ptoBalanceHours !== undefined) updateData.pto_balance_hours = updates?.ptoBalanceHours;
      if (updates?.sickLeaveBalanceHours !== undefined) updateData.sick_leave_balance_hours = updates?.sickLeaveBalanceHours;
      if (updates?.performanceRating !== undefined) updateData.performance_rating = updates?.performanceRating;
      if (updates?.notes !== undefined) updateData.notes = updates?.notes;

      const { data, error } = await supabase?.from('hr_staff_profiles')?.update(updateData)?.eq('id', profileId)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get HR profile by staff ID
  async getHRProfile(staffId) {
    try {
      const { data, error } = await supabase?.from('hr_staff_profiles')?.select(`
          *,
          staff:user_profiles!staff_id(id, full_name, email, phone, avatar_url, role),
          gym:gyms(id, name)
        `)?.eq('staff_id', staffId)?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get all HR profiles for a gym
  async getGymHRProfiles(gymId) {
    try {
      const { data, error } = await supabase?.from('hr_staff_profiles')?.select(`
          *,
          staff:user_profiles!staff_id(id, full_name, email, phone, avatar_url, role),
          gym:gyms(id, name)
        `)?.eq('gym_id', gymId)?.order('hire_date', { ascending: false });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create leave request
  async createLeaveRequest(leaveData) {
    try {
      const { data, error } = await supabase?.from('leave_requests')?.insert([{
          gym_id: leaveData?.gymId,
          staff_id: leaveData?.staffId,
          leave_type: leaveData?.leaveType,
          start_date: leaveData?.startDate,
          end_date: leaveData?.endDate,
          total_days: leaveData?.totalDays,
          reason: leaveData?.reason
        }])?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update leave request
  async updateLeaveRequest(leaveId, updates) {
    try {
      const updateData = {};
      if (updates?.status !== undefined) updateData.status = updates?.status;
      if (updates?.reviewedBy !== undefined) updateData.reviewed_by = updates?.reviewedBy;
      if (updates?.reviewNotes !== undefined) updateData.review_notes = updates?.reviewNotes;
      if (updates?.status) {
        updateData.reviewed_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase?.from('leave_requests')?.update(updateData)?.eq('id', leaveId)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get staff leave requests
  async getStaffLeaveRequests(staffId) {
    try {
      const { data, error } = await supabase?.from('leave_requests')?.select(`
          *,
          staff:user_profiles!staff_id(id, full_name, email),
          reviewer:user_profiles!reviewed_by(id, full_name)
        `)?.eq('staff_id', staffId)?.order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get gym leave requests
  async getGymLeaveRequests(gymId, status = null) {
    try {
      let query = supabase?.from('leave_requests')?.select(`
          *,
          staff:user_profiles!staff_id(id, full_name, email),
          reviewer:user_profiles!reviewed_by(id, full_name)
        `)?.eq('gym_id', gymId)?.order('created_at', { ascending: false });

      if (status) {
        query = query?.eq('status', status);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get staff schedule
  async getStaffSchedule(staffId, startDate = null, endDate = null) {
    try {
      let query = supabase?.from('staff_schedules')?.select(`
          *,
          staff:user_profiles!staff_id(id, full_name, email),
          gym:gyms(id, name)
        `)?.eq('staff_id', staffId)?.order('schedule_date', { ascending: true });

      if (startDate) {
        query = query?.gte('schedule_date', startDate);
      }
      if (endDate) {
        query = query?.lte('schedule_date', endDate);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create staff schedule
  async createSchedule(scheduleData) {
    try {
      const { data, error } = await supabase?.from('staff_schedules')?.insert([{
          gym_id: scheduleData?.gymId,
          staff_id: scheduleData?.staffId,
          schedule_date: scheduleData?.scheduleDate,
          shift_start_time: scheduleData?.shiftStartTime,
          shift_end_time: scheduleData?.shiftEndTime,
          break_duration_minutes: scheduleData?.breakDurationMinutes || 30,
          position: scheduleData?.position,
          notes: scheduleData?.notes
        }])?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// ============================================
// COMPREHENSIVE HR SERVICE EXTENSIONS
// ============================================

export const comprehensiveHRService = {
  // ========== CERTIFICATIONS ==========
  async createCertification(certData) {
    try {
      const { data, error } = await supabase?.from('employee_certifications')?.insert([{
        gym_id: certData?.gymId,
        staff_id: certData?.staffId,
        hr_profile_id: certData?.hrProfileId,
        certification_name: certData?.certificationName,
        certification_type: certData?.certificationType,
        issuing_organization: certData?.issuingOrganization,
        certification_number: certData?.certificationNumber,
        issue_date: certData?.issueDate,
        expiry_date: certData?.expiryDate,
        status: certData?.status || 'active',
        document_url: certData?.documentUrl,
        notes: certData?.notes
      }])?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getStaffCertifications(staffId) {
    try {
      const { data, error } = await supabase?.from('employee_certifications')?.select(`
        *,
        staff:user_profiles!staff_id(id, full_name, email)
      `)?.eq('staff_id', staffId)?.order('expiry_date', { ascending: true });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getExpiringCertifications(gymId, daysThreshold = 30) {
    try {
      const { data, error } = await supabase?.rpc('get_expiring_certifications', {
        p_gym_id: gymId,
        p_days_threshold: daysThreshold
      });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateCertification(certId, updates) {
    try {
      const updateData = {};
      if (updates?.status !== undefined) updateData.status = updates?.status;
      if (updates?.expiryDate !== undefined) updateData.expiry_date = updates?.expiryDate;
      if (updates?.documentUrl !== undefined) updateData.document_url = updates?.documentUrl;
      if (updates?.notes !== undefined) updateData.notes = updates?.notes;

      const { data, error } = await supabase?.from('employee_certifications')?.update(updateData)?.eq('id', certId)?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ========== TRAINER SESSIONS ==========
  async createTrainerSession(sessionData) {
    try {
      const { data, error } = await supabase?.from('trainer_sessions')?.insert([{
        gym_id: sessionData?.gymId,
        trainer_id: sessionData?.trainerId,
        hr_profile_id: sessionData?.hrProfileId,
        member_id: sessionData?.memberId,
        session_type: sessionData?.sessionType,
        session_date: sessionData?.sessionDate,
        start_time: sessionData?.startTime,
        end_time: sessionData?.endTime,
        session_rate: sessionData?.sessionRate,
        commission_rate: sessionData?.commissionRate,
        revenue_generated: sessionData?.revenueGenerated,
        status: sessionData?.status || 'scheduled',
        notes: sessionData?.notes
      }])?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getTrainerSessions(trainerId, startDate = null, endDate = null) {
    try {
      let query = supabase?.from('trainer_sessions')?.select(`
        *,
        trainer:user_profiles!trainer_id(id, full_name, email),
        member:user_profiles!member_id(id, full_name, email)
      `)?.eq('trainer_id', trainerId)?.order('session_date', { ascending: false });

      if (startDate) query = query?.gte('session_date', startDate);
      if (endDate) query = query?.lte('session_date', endDate);

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateTrainerSession(sessionId, updates) {
    try {
      const updateData = {};
      if (updates?.status !== undefined) updateData.status = updates?.status;
      if (updates?.revenueGenerated !== undefined) updateData.revenue_generated = updates?.revenueGenerated;
      if (updates?.notes !== undefined) updateData.notes = updates?.notes;

      const { data, error } = await supabase?.from('trainer_sessions')?.update(updateData)?.eq('id', sessionId)?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ========== SHIFT SCHEDULING ==========
  async createShift(shiftData) {
    try {
      const { data, error } = await supabase?.from('shift_schedules')?.insert([{
        gym_id: shiftData?.gymId,
        staff_id: shiftData?.staffId,
        hr_profile_id: shiftData?.hrProfileId,
        schedule_date: shiftData?.scheduleDate,
        shift_start_time: shiftData?.shiftStartTime,
        shift_end_time: shiftData?.shiftEndTime,
        break_duration_minutes: shiftData?.breakDurationMinutes || 30,
        position: shiftData?.position,
        location: shiftData?.location,
        shift_type: shiftData?.shiftType,
        status: shiftData?.status || 'scheduled',
        assigned_by: shiftData?.assignedBy,
        notes: shiftData?.notes
      }])?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getStaffShifts(staffId, startDate = null, endDate = null) {
    try {
      let query = supabase?.from('shift_schedules')?.select(`
        *,
        staff:user_profiles!staff_id(id, full_name, email),
        assigned_by_user:user_profiles!assigned_by(id, full_name)
      `)?.eq('staff_id', staffId)?.order('schedule_date', { ascending: true });

      if (startDate) query = query?.gte('schedule_date', startDate);
      if (endDate) query = query?.lte('schedule_date', endDate);

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getGymShifts(gymId, date = null) {
    try {
      let query = supabase?.from('shift_schedules')?.select(`
        *,
        staff:user_profiles!staff_id(id, full_name, email, role),
        assigned_by_user:user_profiles!assigned_by(id, full_name)
      `)?.eq('gym_id', gymId)?.order('schedule_date', { ascending: true });

      if (date) query = query?.eq('schedule_date', date);

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async checkShiftConflict(staffId, scheduleDate, startTime, endTime, excludeShiftId = null) {
    try {
      const { data, error } = await supabase?.rpc('check_shift_conflict', {
        p_staff_id: staffId,
        p_schedule_date: scheduleDate,
        p_start_time: startTime,
        p_end_time: endTime,
        p_exclude_shift_id: excludeShiftId
      });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateShift(shiftId, updates) {
    try {
      const updateData = {};
      if (updates?.status !== undefined) updateData.status = updates?.status;
      if (updates?.notes !== undefined) updateData.notes = updates?.notes;
      if (updates?.location !== undefined) updateData.location = updates?.location;

      const { data, error } = await supabase?.from('shift_schedules')?.update(updateData)?.eq('id', shiftId)?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ========== STAFF AVAILABILITY ==========
  async setStaffAvailability(availabilityData) {
    try {
      const { data, error } = await supabase?.from('staff_availability')?.insert([{
        gym_id: availabilityData?.gymId,
        staff_id: availabilityData?.staffId,
        day_of_week: availabilityData?.dayOfWeek,
        start_time: availabilityData?.startTime,
        end_time: availabilityData?.endTime,
        is_available: availabilityData?.isAvailable !== undefined ? availabilityData?.isAvailable : true,
        effective_from: availabilityData?.effectiveFrom,
        effective_until: availabilityData?.effectiveUntil,
        notes: availabilityData?.notes
      }])?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getStaffAvailability(staffId) {
    try {
      const { data, error } = await supabase?.from('staff_availability')?.select('*')?.eq('staff_id', staffId)?.order('day_of_week', { ascending: true });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ========== PERFORMANCE REVIEWS ==========
  async createPerformanceReview(reviewData) {
    try {
      const { data, error } = await supabase?.from('performance_reviews')?.insert([{
        gym_id: reviewData?.gymId,
        staff_id: reviewData?.staffId,
        hr_profile_id: reviewData?.hrProfileId,
        reviewer_id: reviewData?.reviewerId,
        review_cycle: reviewData?.reviewCycle,
        review_date: reviewData?.reviewDate,
        review_period_start: reviewData?.reviewPeriodStart,
        review_period_end: reviewData?.reviewPeriodEnd,
        punctuality_rating: reviewData?.punctualityRating,
        performance_rating: reviewData?.performanceRating,
        client_feedback_rating: reviewData?.clientFeedbackRating,
        revenue_contribution: reviewData?.revenueContribution,
        sessions_completed: reviewData?.sessionsCompleted,
        attendance_rate: reviewData?.attendanceRate,
        strengths: reviewData?.strengths,
        areas_for_improvement: reviewData?.areasForImprovement,
        goals: reviewData?.goals,
        manager_notes: reviewData?.managerNotes,
        overall_rating: reviewData?.overallRating
      }])?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getStaffReviews(staffId) {
    try {
      const { data, error } = await supabase?.from('performance_reviews')?.select(`
        *,
        staff:user_profiles!staff_id(id, full_name, email),
        reviewer:user_profiles!reviewer_id(id, full_name)
      `)?.eq('staff_id', staffId)?.order('review_date', { ascending: false });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getGymReviews(gymId) {
    try {
      const { data, error } = await supabase?.from('performance_reviews')?.select(`
        *,
        staff:user_profiles!staff_id(id, full_name, email, role),
        reviewer:user_profiles!reviewer_id(id, full_name)
      `)?.eq('gym_id', gymId)?.order('review_date', { ascending: false });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ========== COMPLIANCE DOCUMENTS ==========
  async createComplianceDocument(docData) {
    try {
      const { data, error } = await supabase?.from('compliance_documents')?.insert([{
        gym_id: docData?.gymId,
        staff_id: docData?.staffId,
        hr_profile_id: docData?.hrProfileId,
        document_type: docData?.documentType,
        document_name: docData?.documentName,
        document_url: docData?.documentUrl,
        issue_date: docData?.issueDate,
        expiry_date: docData?.expiryDate,
        is_verified: docData?.isVerified || false,
        verified_by: docData?.verifiedBy,
        notes: docData?.notes
      }])?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getStaffDocuments(staffId) {
    try {
      const { data, error } = await supabase?.from('compliance_documents')?.select(`
        *,
        staff:user_profiles!staff_id(id, full_name, email),
        verifier:user_profiles!verified_by(id, full_name)
      `)?.eq('staff_id', staffId)?.order('created_at', { ascending: false });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async verifyDocument(docId, verifierId) {
    try {
      const { data, error } = await supabase?.from('compliance_documents')?.update({
        is_verified: true,
        verified_by: verifierId,
        verified_at: new Date()?.toISOString()
      })?.eq('id', docId)?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ========== HR NOTIFICATIONS ==========
  async createNotification(notifData) {
    try {
      const { data, error } = await supabase?.from('hr_notifications')?.insert([{
        gym_id: notifData?.gymId,
        staff_id: notifData?.staffId,
        notification_type: notifData?.notificationType,
        priority: notifData?.priority || 'medium',
        title: notifData?.title,
        message: notifData?.message,
        related_entity_type: notifData?.relatedEntityType,
        related_entity_id: notifData?.relatedEntityId,
        scheduled_for: notifData?.scheduledFor
      }])?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getStaffNotifications(staffId, unreadOnly = false) {
    try {
      let query = supabase?.from('hr_notifications')?.select('*')?.eq('staff_id', staffId)?.order('created_at', { ascending: false });

      if (unreadOnly) query = query?.eq('is_read', false);

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async markNotificationRead(notifId) {
    try {
      const { data, error } = await supabase?.from('hr_notifications')?.update({
        is_read: true,
        read_at: new Date()?.toISOString()
      })?.eq('id', notifId)?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ========== TRAINER METRICS ==========
  async getTrainerMetrics(trainerId, startDate = null, endDate = null) {
    try {
      let query = supabase?.from('trainer_metrics')?.select('*')?.eq('trainer_id', trainerId)?.order('metric_date', { ascending: false });

      if (startDate) query = query?.gte('metric_date', startDate);
      if (endDate) query = query?.lte('metric_date', endDate);

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateTrainerMetrics(trainerId, metricDate) {
    try {
      const { data, error } = await supabase?.rpc('update_trainer_metrics', {
        p_trainer_id: trainerId,
        p_metric_date: metricDate
      });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // ========== PAYROLL COMPONENTS ==========
  async addPayrollComponent(componentData) {
    try {
      const { data, error } = await supabase?.from('payroll_components')?.insert([{
        payroll_record_id: componentData?.payrollRecordId,
        component_type: componentData?.componentType,
        component_name: componentData?.componentName,
        amount: componentData?.amount,
        quantity: componentData?.quantity,
        rate: componentData?.rate,
        description: componentData?.description
      }])?.select()?.single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getPayrollComponents(payrollRecordId) {
    try {
      const { data, error } = await supabase?.from('payroll_components')?.select('*')?.eq('payroll_record_id', payrollRecordId)?.order('created_at', { ascending: true });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }
};